import { useCallback, useEffect } from 'react';

// New GA4 Measurement ID
const MEASUREMENT_ID = 'G-VYT7GJ6D2W';

// Hook personalizado para Google Analytics GA4
const useGoogleAnalytics = () => {
  
  // Initialize GA4 once
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.gtag) {
      // Load Google Analytics script
      const script1 = document.createElement('script');
      script1.async = true;
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
      document.head.appendChild(script1);

      // Initialize gtag
      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${MEASUREMENT_ID}');
      `;
      document.head.appendChild(script2);

      // Make gtag available globally
      window.gtag = window.gtag || function(){window.dataLayer.push(arguments);};
    }
  }, []);
  
  // Función para enviar eventos personalizados
  const trackEvent = useCallback((eventName, parameters = {}) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        ...parameters,
        // Agregar timestamp para debugging
        custom_timestamp: new Date().toISOString()
      });
      
      // Log para desarrollo
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 GA4 Event:', eventName, parameters);
      }
    }
  }, []);

  // Eventos específicos para tu negocio
  const events = {
    // CONVERSIONES PRINCIPALES
    requestDemo: (formData = {}) => {
      trackEvent('form_submit', {
        form_name: 'demo_request',
        form_location: 'main_page',
        user_email: formData.email || 'unknown',
        company: formData.company || 'unknown',
        value: 100 // Valor estimado de un lead
      });
    },

    bookDemo: (appName) => {
      trackEvent('book_demo_click', {
        app_name: appName,
        value: 150
      });
    },

    // HOMEPAGE 2026
    homeBookDemo: (location = 'unknown') => {
      trackEvent('home_book_demo_click', {
        button_location: location,
        value: 150
      });
    },

    exploreSolutions: (location = 'hero') => {
      trackEvent('explore_solutions_click', {
        button_location: location
      });
    },

    scheduleDiscovery: (location = 'final_cta') => {
      trackEvent('schedule_discovery_click', {
        button_location: location,
        value: 120
      });
    },

    viewSolutionCard: (serviceId) => {
      trackEvent('solution_card_click', {
        service_id: serviceId
      });
    },

    clickTech: (techName, group) => {
      trackEvent('tech_ecosystem_click', {
        tech_name: techName,
        tech_group: group
      });
    },

    scheduleConsultation: (location = 'unknown') => {
      trackEvent('schedule_consultation', {
        button_location: location,
        value: 200 // Valor estimado de consultoría
      });
    },

    installApp: (appName, appUrl) => {
      trackEvent('app_install_click', {
        app_name: appName,
        app_url: appUrl,
        value: 50
      });
    },

    completedConfigurator: (answers = {}) => {
      trackEvent('configurator_completed', {
        zendesk_plan: answers.currentPlan || 'unknown',
        agent_count: answers.agentCount || 'unknown',
        channels_count: answers.channels?.length || 0,
        value: 150
      });
    },

    viewPricing: () => {
      trackEvent('view_pricing', {
        page_location: 'pricing_page'
      });
    },

    // ENGAGEMENT CON CONTENIDO
    watchDemo: (appName, progress = 0) => {
      trackEvent('video_play', {
        video_title: `${appName}_demo`,
        video_progress: progress
      });
    },

    configuratorProgress: (step, answers = {}) => {
      trackEvent('configurator_progress', {
        step_number: step,
        step_completed: true,
        total_progress: Math.round((step / 8) * 100)
      });
    },

    // NAVEGACIÓN
    pageView: (pageName, additionalData = {}) => {
      trackEvent('page_view', {
        page_title: pageName,
        ...additionalData
      });
    },

    clickNavigation: (destination) => {
      trackEvent('navigation_click', {
        destination: destination
      });
    },

    // ZENDESK PROMPTS
    viewPrompt: (promptTitle, promptIndex) => {
      trackEvent('view_prompt', {
        prompt_title: promptTitle,
        prompt_index: promptIndex,
        page_location: 'app_builder'
      });
    },

    copyPrompt: (promptTitle, promptIndex) => {
      trackEvent('copy_prompt', {
        prompt_title: promptTitle,
        prompt_index: promptIndex,
        page_location: 'app_builder',
        value: 25 // Value for copying a prompt
      });
    },

    clickResource: (resourceName, resourceUrl) => {
      trackEvent('resource_click', {
        resource_name: resourceName,
        resource_url: resourceUrl,
        page_location: 'started_guide'
      });
    },

    // SLA SIMULATOR EVENTS
    startSlaSimulation: (hasTargets, targetData = {}) => {
      trackEvent('sla_simulation_started', {
        has_targets: hasTargets,
        targets_configured: Object.keys(targetData).length,
        value: 30
      });
    },

    addSlaEvent: (eventType, ticketStatus, elapsedTime) => {
      trackEvent('sla_event_added', {
        event_type: eventType,
        ticket_status: ticketStatus,
        elapsed_seconds: Math.round(elapsedTime),
        page_location: 'sla_simulator'
      });
    },

    completeSlaSimulation: (metrics, hasTargets, achievements = {}) => {
      trackEvent('sla_simulation_completed', {
        resolution_time: Math.round(metrics.resolutionTime),
        requester_wait_time: Math.round(metrics.requesterWaitTime),
        agent_work_time: Math.round(metrics.agentWorkTime),
        first_reply_time: metrics.firstReplyTime ? Math.round(metrics.firstReplyTime) : null,
        has_targets: hasTargets,
        targets_achieved: achievements.totalAchieved || 0,
        targets_breached: achievements.totalBreached || 0,
        value: 40
      });
    },

    resetSlaSimulation: () => {
      trackEvent('sla_simulation_reset', {
        page_location: 'sla_simulator'
      });
    },

    pauseSlaSimulation: (elapsedTime) => {
      trackEvent('sla_simulation_paused', {
        elapsed_seconds: Math.round(elapsedTime),
        page_location: 'sla_simulator'
      });
    },

    resumeSlaSimulation: (elapsedTime) => {
      trackEvent('sla_simulation_resumed', {
        elapsed_seconds: Math.round(elapsedTime),
        page_location: 'sla_simulator'
      });
    },

    // ZENDESK GUIDE EVENTS
    openGuideModal: () => {
      trackEvent('guide_modal_opened', {
        page_location: 'home_banner'
      });
    },

    downloadGuide: (email) => {
      trackEvent('guide_download_request', {
        user_email: email,
        value: 80 // Potential lead value
      });
    },

    // PROMO WORKBOOK
    buyWorkbookClick: (lang) => {
      trackEvent('buy_workbook_click', {
        workbook_lang: lang,
        value: 50
      });
    },

    watchWorkbookDemo: (lang) => {
      trackEvent('watch_workbook_demo', {
        workbook_lang: lang,
        value: 10
      });
    },

    // ZENDESK OCR (Omnichannel Routing guide)
    buyZendeskOcr: (lang, location = 'zendesk_ocr_page') => {
      trackEvent('buy_zendesk_ocr_click', {
        product_lang: lang,
        page_location: location,
        value: 50
      });
    },

    // ZENDESK ACTION FLOWS GUIDE
    buyActionFlowsGuide: (lang) => {
      trackEvent('buy_action_flows_guide_click', {
        guide_lang: lang,
        page_location: 'zendesk_action_flows',
        value: 50
      });
    }
  };

  return { trackEvent, events };
};

export default useGoogleAnalytics; 